import type { Demand, DemandInput } from '@aimmo/contracts';
import {
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  DEMAND_STATUSES,
  DEMAND_STATUS_LABELS,
  PROPERTY_USES,
  PROPERTY_USE_LABELS,
  demandCreateSchema,
  enumOptions,
} from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { resolveComune, searchComuni } from './geo-lookups.js';
import { resolveContact, searchContacts } from './contact-lookups.js';
import { resolveTypology, resolveZone, searchTypologies, searchZones } from './property-lookups.js';
import { resolveUser, searchUsers } from './mandate-lookups.js';
import { AttributesField } from './property-attributes-field.js';
import { CriteriaPicker } from './criteria-picker.js';
import { DemandComuniField } from './demand-comuni-field.js';
import { DemandMatches } from './demand-matches.js';
import { ActivityTimeline } from '../features/activity-timeline.js';
import { formatPrice, formatScalar } from '../lib/format.js';

const STATUS_OPTIONS = enumOptions(DEMAND_STATUSES, DEMAND_STATUS_LABELS);
const CONTRACT_OPTIONS = enumOptions(CONTRACT_TYPES, CONTRACT_TYPE_LABELS);
const USE_OPTIONS = enumOptions(PROPERTY_USES, PROPERTY_USE_LABELS);

const STATUS_TONES = {
  open: 'success',
  working: 'accent',
  won: 'neutral',
  lost: 'neutral',
} as const;

/**
 * A buyer's search profile (docs/domain/07-demands-and-matching.md).
 *
 * Everything on the form is a criterion. The matching workbench sits under the
 * detail and recomputes on every visit, so what an agent sees is what is true
 * now rather than what was true when the demand was saved.
 */
export const demandsResource = defineResource<Demand, DemandInput, DemandInput, DemandInput>({
  key: 'demands',
  label: { one: 'Richiesta', many: 'Richieste', gender: 'f' },
  client: tsRestResourceClient(api.demands),
  getRowId: (row) => row.id,
  defaultState: { sort: 'createdAt', order: 'desc' },
  search: { placeholder: 'Nome del richiedente, note…' },
  columns: [
    { id: 'contactName', header: 'Richiedente' },
    {
      id: 'status',
      header: 'Stato',
      width: '9rem',
      cell: ({ row }) => (
        <Badge tone={STATUS_TONES[row.status]}>{DEMAND_STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      id: 'contractType',
      header: 'Contratto',
      width: '8rem',
      accessor: (row) => CONTRACT_TYPE_LABELS[row.contractType],
    },
    {
      id: 'budgetMax',
      header: 'Budget',
      sortKey: 'budgetMax',
      align: 'right',
      width: '9rem',
      accessor: (row) => formatPrice(row.budgetMax, ''),
    },
    { id: 'surfaceMinMq', header: 'mq min', align: 'right', width: '7rem' },
    { id: 'localiMin', header: 'Locali min', align: 'right', width: '8rem' },
    {
      id: 'areas',
      header: 'Aree',
      accessor: (row) => [...row.zonaNames, ...row.comuneNames].join(', '),
    },
    { id: 'agentName', header: 'Agente', hiddenByDefault: true },
  ],
  filters: [
    { id: 'status', label: 'Stato', type: 'enum', options: STATUS_OPTIONS },
    { id: 'contractType', label: 'Contratto', type: 'enum', options: CONTRACT_OPTIONS },
    { id: 'use', label: 'Utilizzo', type: 'enum', options: USE_OPTIONS },
    {
      id: 'contactId',
      label: 'Richiedente',
      type: 'relation',
      search: searchContacts,
      resolve: resolveContact,
    },
    {
      id: 'comuneId',
      label: 'Comune',
      type: 'relation',
      search: searchComuni,
      resolve: resolveComune,
    },
    {
      id: 'agentUserId',
      label: 'Agente',
      type: 'relation',
      search: searchUsers,
      resolve: resolveUser,
    },
  ],
  form: {
    schema: demandCreateSchema,
    defaultValues: {
      contactId: '',
      agentUserId: '',
      contractType: 'sale',
      use: 'residential',
      status: 'open',
      typologyIds: [],
      comuneIds: [],
      zonaIds: [],
      budgetMin: '',
      budgetMax: '',
      surfaceMinMq: null,
      localiMin: null,
      lat: null,
      lng: null,
      radiusKm: null,
      privacyConsent: false,
      desiredAttributes: {},
      notes: '',
    },
    fromItem: (demand) => {
      return {
        contactId: demand.contactId,
        agentUserId: demand.agentUserId ?? '',
        contractType: demand.contractType,
        use: demand.use,
        status: demand.status,
        typologyIds: demand.typologyIds,
        comuneIds: demand.comuneIds,
        zonaIds: demand.zonaIds,
        budgetMin: demand.budgetMin ?? '',
        budgetMax: demand.budgetMax ?? '',
        surfaceMinMq: demand.surfaceMinMq,
        localiMin: demand.localiMin,
        lat: demand.lat,
        lng: demand.lng,
        radiusKm: demand.radiusKm,
        // Consent already given stays given; the switch reflects it and
        // clearing it here does not revoke anything.
        privacyConsent: demand.privacyConsentAt !== null,
        desiredAttributes: demand.desiredAttributes as DemandInput['desiredAttributes'],
        notes: demand.notes ?? '',
      };
    },
    sections: [
      {
        id: 'who',
        title: 'Richiedente',
        fields: [
          {
            name: 'contactId',
            label: 'Contatto',
            type: 'relation',
            required: true,
            search: searchContacts,
            resolve: resolveContact,
          },
          {
            name: 'agentUserId',
            label: 'Agente',
            type: 'relation',
            search: searchUsers,
            resolve: resolveUser,
          },
          {
            name: 'status',
            label: 'Stato',
            type: 'select',
            required: true,
            options: STATUS_OPTIONS,
          },
          {
            name: 'privacyConsent',
            label: 'Consenso al trattamento dei dati',
            type: 'switch',
            description: 'Registra data e ora: la revoca è un atto a sé.',
          },
        ],
      },
      {
        id: 'what',
        title: 'Cosa cerca',
        fields: [
          {
            name: 'contractType',
            label: 'Contratto',
            type: 'select',
            required: true,
            options: CONTRACT_OPTIONS,
          },
          { name: 'use', label: 'Utilizzo', type: 'select', required: true, options: USE_OPTIONS },
          {
            name: 'typologyIds',
            label: 'Tipologie',
            type: 'custom',
            wide: true,
            description: 'Nessuna selezione significa "qualsiasi tipologia".',
            render: (props) => (
              <CriteriaPicker
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
                id={props.id}
                search={searchTypologies}
                resolve={resolveTypology}
                placeholder="Cerca tipologia…"
              />
            ),
          },
        ],
      },
      {
        id: 'where',
        title: 'Dove',
        description: 'Comune, zona o raggio: chi ne indica due intende l’uno o l’altro.',
        fields: [
          {
            name: 'comuneIds',
            label: 'Comuni',
            type: 'custom',
            render: (props) => (
              <DemandComuniField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
                id={props.id}
              />
            ),
          },
          {
            name: 'zonaIds',
            label: 'Zone',
            type: 'custom',
            render: (props) => (
              <CriteriaPicker
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
                id={props.id}
                search={searchZones}
                resolve={resolveZone}
                placeholder="Cerca zona…"
              />
            ),
          },
          { name: 'lat', label: 'Latitudine', type: 'number', step: 0.000001 },
          { name: 'lng', label: 'Longitudine', type: 'number', step: 0.000001 },
          { name: 'radiusKm', label: 'Raggio (km)', type: 'number', min: 0.1, step: 0.5 },
        ],
      },
      {
        id: 'budget',
        title: 'Budget e requisiti',
        fields: [
          { name: 'budgetMin', label: 'Budget minimo (€)', type: 'text' },
          {
            name: 'budgetMax',
            label: 'Budget massimo (€)',
            type: 'text',
            placeholder: '300000.00',
          },
          { name: 'surfaceMinMq', label: 'Superficie minima (mq)', type: 'number', min: 0 },
          { name: 'localiMin', label: 'Locali minimi', type: 'number', min: 0 },
        ],
      },
      {
        id: 'preferences',
        title: 'Preferenze',
        description: 'Pesano sul punteggio, non escludono: giardino, ascensore, piano alto.',
        columns: 1,
        fields: [
          {
            name: 'desiredAttributes',
            label: 'Preferenze',
            type: 'custom',
            wide: true,
            render: (props) => (
              <AttributesField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
              />
            ),
          },
          { name: 'notes', label: 'Note', type: 'textarea', rows: 4, wide: true },
        ],
      },
    ],
  },
  detail: {
    title: (row) => `${row.contactName} · ${CONTRACT_TYPE_LABELS[row.contractType]}`,
    after: (row) => (
      <div className="grid gap-[var(--card-gap)]">
        <DemandMatches demandId={row.id} />
        <ActivityTimeline subjectType="demand" subjectId={row.id} />
      </div>
    ),
    sections: [
      {
        id: 'criteria',
        title: 'Criteri',
        columns: 3,
        fields: [
          {
            id: 'status',
            label: 'Stato',
            render: (row) => (
              <Badge tone={STATUS_TONES[row.status]}>{DEMAND_STATUS_LABELS[row.status]}</Badge>
            ),
          },
          { id: 'use', label: 'Utilizzo', accessor: (row) => PROPERTY_USE_LABELS[row.use] },
          { id: 'agentName', label: 'Agente' },
          {
            id: 'budget',
            label: 'Budget',
            accessor: (row) =>
              [row.budgetMin, row.budgetMax].some(Boolean)
                ? `${formatPrice(row.budgetMin || null)} – ${formatPrice(row.budgetMax || null)}`
                : '—',
          },
          { id: 'surfaceMinMq', label: 'Superficie minima (mq)' },
          { id: 'localiMin', label: 'Locali minimi' },
          {
            id: 'typologyLabels',
            label: 'Tipologie',
            accessor: (row) =>
              row.typologyLabels.length > 0 ? row.typologyLabels.join(', ') : 'Qualsiasi',
          },
          {
            id: 'areas',
            label: 'Aree',
            accessor: (row) =>
              [...row.zonaNames, ...row.comuneNames].join(', ') ||
              (row.lat !== null ? `${row.radiusKm ?? 0} km dal punto indicato` : 'Qualsiasi'),
          },
          {
            id: 'privacyConsentAt',
            label: 'Consenso privacy',
            accessor: (row) => (row.privacyConsentAt ? new Date(row.privacyConsentAt) : 'Assente'),
          },
        ],
      },
      {
        id: 'preferences',
        title: 'Preferenze',
        render: (row) => {
          const entries = Object.entries(row.desiredAttributes);
          if (entries.length === 0) {
            return <p className="text-secondary text-muted">Nessuna preferenza indicata.</p>;
          }

          return (
            <ul className="flex flex-wrap gap-1">
              {entries.map(([key, value]) => (
                <li key={key}>
                  <Badge tone="neutral">
                    {key}: {formatScalar(value)}
                  </Badge>
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        id: 'notes',
        title: 'Note',
        columns: 1,
        fields: [{ id: 'notes', label: 'Note' }],
      },
    ],
  },
  empty: {
    title: 'Nessuna richiesta',
    description: 'Una richiesta è il profilo di ricerca di un acquirente: cosa, dove, quanto.',
  },
});
